// src/utils/fillDummyValues.js
import {
  EDU_MLUS,
  GOV_MLUS,
  TEC_MLUS,
  EID_MLUS,
} from "../components/FrameworkSelector";

/**
 * Factory that returns a fillDummyValues() function bound to your setters.
 * You call it inside App after all useState hooks.
 */
export const createFillDummyValues = ({
  setOrgGoal,
  setCertificationNeeded,
  setProfile,
  setFutureRoles,
  setExpectedContribution,
  setSelEdu,
  setSelGov,
  setSelTech,
  setSelEth,
  setAlignmentNotes,
  setNotesByFramework,
}) => {
  return () => {
    /**
     * STEP 1 – ORGANISATIONAL GOAL
     *
     * Suggested template for users:
     *  1) Context: [city/department situation]
     *  2) Main challenge: [what is missing today]
     *  3) Capacity-building goal: [what they want staff to learn/do]
     *  4) Desired change: [how decisions/collaboration should improve]
     *  5) Success: [1–2 outcome signals]
     *
     * Target length: 50–100 words.
     */
    setOrgGoal(
      "1) Context: Our mid-sized European city is starting to explore Local Digital Twins to support mobility, climate and participation projects. " +
        "2) Main challenge: Knowledge and responsibilities are fragmented across departments. " +
        "3) Capacity-building goal: Build shared literacy around data workflows, governance and ethics so staff can work with pilots confidently. " +
        "4) Desired change: Teams can question LDT model assumptions, interpret LLM summaries and translate insights into realistic actions. " +
        "5) Success: More cross-department projects using the twin and clearer communication of results."
    );
    if (setCertificationNeeded) {
      setCertificationNeeded(false);
    }

    /**
     * STEP 2 – LEARNER PROFILE
     *
     * Suggested structure for users:
     *  - Job title: [role + main area]
     *  - Job experience: [years + 1–2 core responsibilities]
     *  - Education background: [highest degree + relevant extra training]
     *  - LDT connection: [how close they are to LDT work today]
     *  - Learning motivation: [what they want to be able to do better]
     *
     * Note: keep each text field under ~500 characters.
     */
    setProfile({
      job_title: "Urban Data Analyst (Mobility Department)",
      job_experience:
        "Three years working with transport datasets, producing dashboards for traffic flow, public transport punctuality and monthly KPIs used by planners and management.",
      education_background:
        "Bachelor in Urban Studies and a postgraduate certificate in Applied Data Analytics with a focus on GIS, statistics and basic programming.",
      ldt_connection:
        "The learner is loosely connected to Local Digital Twin discussions and has experimented with data exports for small pilot dashboards.",
      learning_motivation:
        "I want to move from producing static reports to actively shaping Local Digital Twin projects. I hope to better understand data pipelines, governance and ethics so I can explain model assumptions, support cross-department workshops, and help decision makers use simulations and AI-generated summaries in a more transparent and responsible way.",
      is_group_mode: false,
    });

    /**
     * STEP 3 – FUTURE ROLES & EXPECTED CONTRIBUTION
     *
     * Suggested structure for users:
     *  - 2–3 future roles chosen from the list.
     *  - Expected contribution:
     *      1) Short role description,
     *      2) What they will do differently day-to-day,
     *      3) How this supports the organisation goal.
     */
    setFutureRoles([
      "Enabler of Data and Modeling Literacy",
      "Advocate for Inclusive Participation",
      "Designer of the Digital Twin Infrastructure",
    ]);

    setExpectedContribution(
      "I aim to connect mobility data with other city datasets, help colleagues understand what Local Digital Twin models can and cannot say, and co-design simple explorable views for non-technical staff and citizens. In my role I want to document assumptions, highlight data gaps, and support workshops where planners, IT staff, and decision makers interpret results together and agree on realistic next steps for pilot projects and follow-up actions."
    );

    /**
     * STEP 4 – CURRENT KNOWLEDGE (SELECTED MLUs)
     *
     * Suggested pattern:
     *  - A mixed subset of beginner + intermediate MLUs
     *  - Spread across EDU, GOV, TEC, EID
     */
    const eduSelected = EDU_MLUS.filter((m) =>
      ["101", "102", "201", "203"].some((suffix) => m.code.endsWith(suffix))
    ).map((m) => m.code);

    const govSelected = GOV_MLUS.filter((m) =>
      ["101", "103", "201"].some((suffix) => m.code.endsWith(suffix))
    ).map((m) => m.code);

    const techSelected = TEC_MLUS.filter((m) =>
      ["101", "102", "202", "203"].some((suffix) => m.code.endsWith(suffix))
    ).map((m) => m.code);

    const eidSelected = EID_MLUS.filter((m) =>
      ["101", "102", "201"].some((suffix) => m.code.endsWith(suffix))
    ).map((m) => m.code);

    setSelEdu(eduSelected);
    setSelGov(govSelected);
    setSelTech(techSelected);
    setSelEth(eidSelected);

    /**
     * STEP 6 – ALIGNMENT NOTES (FREE TEXT)
     *
     * Suggested template for users:
     *  (A) Overall strategy for this journey
     *  (B) EDU / GOV / TEC / EID focus and trade-offs
     *  (C) What I changed compared to the initial LLM suggestions
     *  (D) Open questions or risks I want to revisit later
     */
    setAlignmentNotes(
      "(A) Overall strategy: Prioritise a solid foundation in EDU and TEC so the learner can understand core LDT concepts and data flows, while keeping a visible GOV and EID thread about governance, ethics and participation.\n\n" +
        "(B) Framework focus: EDU and TEC are slightly stronger in the early weeks; GOV and EID are woven in where they directly support mobility use cases and cooperation with other departments.\n\n" +
        "(C) Changes vs. initial LLM suggestions: I removed some advanced TEC units that felt too technical for the current role and added more GOV/EID units that speak to cross-department collaboration and citizen-facing communication.\n\n" +
        "(D) Open questions: How much time the learner realistically has per week, and whether other departments will join a shared learning cohort for discussion sessions."
    );

        /**
     * STEP 6 – NOTES BY FRAMEWORK (STRUCTURED)
     *
     * Suggested template per framework (max ~100 words):
     *  - State the main purpose of this block for your learner or team.
     *  - Explain briefly why these MLUs are a good next step (link to roles, goals, or gaps).
     *  - Mention any planned follow-up activities (workshops, internal meetings, experiments).
     *  - Keep it concrete and practical – imagine you are explaining this to a colleague.
     */
    setNotesByFramework({
      education:
        "This EDU block helps our urban data analyst put a clear, shared language around Local Digital Twins into practice for mobility work. " +
        "The chosen MLUs are a good next step because they connect basic LDT concepts to their existing dashboards and reports. " +
        "After this block, I plan a short internal brown-bag where they walk colleagues through one concrete mobility case using the new terminology.",

      governance:
        "This GOV block helps the learner see where their mobility analysis fits into real decision processes and responsibilities. " +
        "These MLUs are a good next step because they clarify who decides what, where risks sit, and how participation can be organised. " +
        "As a follow-up, we will map the governance chain for one pilot project together and compare it to the patterns shown in the MLUs.",

      technical:
        "This TEC block positions the learner as a bridge between analysis and IT teams, not as a full engineer. " +
        "The selected MLUs focus on data flows, basic model structures and interfaces directly relevant to mobility pilots, which matches their role. " +
        "Afterwards, I plan a joint session with an IT colleague to map one existing data pipeline against the LDT building blocks and identify quick wins.",

      ethics:
        "This EID block brings fairness, inclusion and explainability into the learner’s everyday mobility work. " +
        "These MLUs are a good next step because they highlight who is visible or invisible in current datasets and how LDT outputs may affect different neighbourhoods. " +
        "As a follow-up, we will run a short workshop on one project to list affected groups and discuss how to communicate limits and trade-offs in plain language.",
    });

  };
};

/**
 * Factory that returns a fillDummyValuesCertification() function that selects
 * properly paired MLUs and enables certification mode.
 */
export const createFillDummyValuesCertification = ({
  setOrgGoal,
  setCertificationNeeded,
  setProfile,
  setFutureRoles,
  setExpectedContribution,
  setSelEdu,
  setSelGov,
  setSelTech,
  setSelEth,
  setAlignmentNotes,
  setNotesByFramework,
}) => {
  return () => {
    setOrgGoal(
      "1) Context: Our mid-sized European city is looking to certify its urban planners in LDT methodology. " +
        "2) Main challenge: No formal certification path exists today. " +
        "3) Capacity-building goal: Complete a guided certification journey covering all four pillars. " +
        "4) Desired change: Certified staff can lead LDT procurement and implementation. " +
        "5) Success: 10 staff members fully certified by end of year."
    );

    if (setCertificationNeeded) {
      setCertificationNeeded(true);
    }

    setProfile({
      job_title: "Senior Urban Planner",
      job_experience: "Ten years in spatial planning. Leading the new digital twin integration initiative.",
      education_background: "Master's in Urban Planning, Certification in Project Management.",
      ldt_connection: "Leading the core team that will procure and manage the upcoming city-wide Digital Twin.",
      learning_motivation: "I need to be formally certified to ensure I have the comprehensive, officially recognised knowledge to oversee our multi-million euro Local Digital Twin project safely and effectively. As the designated lead for this major city-wide initiative, my responsibilities span multiple complex domains including technical architecture, data governance, and ethical deployment. Obtaining this formal qualification will give me the confidence and authority to challenge external vendors and align internal stakeholders.",
      is_group_mode: false,
    });

    setFutureRoles([
      "Designer of the Digital Twin Infrastructure",
      "Governance Framework",
    ]);

    setExpectedContribution(
      "In my upcoming role as the lead of the Digital Twin task force, I will be the primary decision maker for the LDT architecture and its underlying governance structure, ensuring we meet all national certification standards. I expect to oversee the technical implementation while coordinating between divergent municipal departments, translating complex modelling results into actionable urban policy recommendations that align with our long-term sustainable development goals for the city."
    );

    // In Certification mode, we MUST select pairs (x01,x02), (x03,x04), etc.
    const eduSelected = EDU_MLUS.filter((m) =>
      ["101", "102", "201", "202"].some((suffix) => m.code.endsWith(suffix))
    ).map((m) => m.code);

    const govSelected = GOV_MLUS.filter((m) =>
      ["101", "102", "203", "204"].some((suffix) => m.code.endsWith(suffix))
    ).map((m) => m.code);

    const techSelected = TEC_MLUS.filter((m) =>
      ["101", "102", "201", "202"].some((suffix) => m.code.endsWith(suffix))
    ).map((m) => m.code);

    const eidSelected = EID_MLUS.filter((m) =>
      ["101", "102", "301", "302"].some((suffix) => m.code.endsWith(suffix))
    ).map((m) => m.code);

    setSelEdu(eduSelected);
    setSelGov(govSelected);
    setSelTech(techSelected);
    setSelEth(eidSelected);

    setAlignmentNotes("Overall strategy: Complete the certification pathway covering core foundational pairs across all frameworks.");

    setNotesByFramework({
      education: "Foundational paired MLUs in education for certification.",
      governance: "Core governance pairs required for procurement certification.",
      technical: "Essential technical pairs for understanding the LDT architecture.",
      ethics: "Critical ethics and inclusion pairs for certified planners.",
    });
  };
};

